import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Heart, ShoppingBag, Star, Minus, Plus, ArrowLeft, Check, MapPin, MessageCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { trackViewItem } from '../hooks/useGoogleAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const { token, isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isInWishlist, setIsInWishlist] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchProduct();
    fetchReviews();
    if (isAuthenticated) {
      checkWishlist();
    }
  }, [id, isAuthenticated]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      
      // Track GA4 view_item event
      trackViewItem(response.data);
    } catch (error) {
      // Product not found or server error - set null to show 404 state
      setProduct(null);
      toast.error('No se pudo cargar el producto');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/${id}`);
      setReviews(response.data);
    } catch (error) {
      // Reviews unavailable - show empty list silently (non-critical feature)
      setReviews([]);
    }
  };

  const checkWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist/check/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsInWishlist(response.data.in_wishlist);
    } catch (error) {
      // Wishlist check failed - default to not in wishlist (non-critical)
      setIsInWishlist(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price) + ' MXN';
  };

  const handleAddToCart = async () => {
    if (product.stock <= 0) {
      toast.error('Producto agotado');
      return;
    }

    if (!isAuthenticated) {
      toast.error('Inicia sesión para agregar al carrito');
      return;
    }

    try {
      setAddingToCart(true);
      await addToCart(id, quantity);
      toast.success('Agregado al carrito');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al agregar al carrito';
      toast.error(message);
    } finally {
      setAddingToCart(false);
    }
  };

  const handleWishlistToggle = async () => {
    if (!isAuthenticated) {
      toast.error('Inicia sesión para guardar favoritos');
      return;
    }

    try {
      if (isInWishlist) {
        await axios.delete(`${API}/wishlist/remove/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsInWishlist(false);
        toast.success('Eliminado de favoritos');
      } else {
        await axios.post(
          `${API}/wishlist/add`,
          { product_id: id },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIsInWishlist(true);
        toast.success('Agregado a favoritos');
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error('Inicia sesión para dejar una reseña');
      return;
    }

    try {
      setSubmittingReview(true);
      await axios.post(
        `${API}/reviews`,
        {
          product_id: id,
          rating: reviewRating,
          comment: reviewComment
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reseña publicada');
      setShowReviewForm(false);
      setReviewComment('');
      setReviewRating(5);
      fetchReviews();
      fetchProduct();
    } catch (error) {
      if (error.response?.data?.detail === 'You have already reviewed this product') {
        toast.error('Ya has publicado una reseña para este producto');
      } else {
        toast.error('Error al publicar reseña');
      }
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#FAFAF9]">
        <div className="container-luxury py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-[#F5F0EB] rounded-2xl animate-pulse"></div>
            <div className="space-y-6">
              <div className="h-4 bg-[#F5F0EB] rounded w-1/4"></div>
              <div className="h-10 bg-[#F5F0EB] rounded w-3/4"></div>
              <div className="h-6 bg-[#F5F0EB] rounded w-1/3"></div>
              <div className="h-24 bg-[#F5F0EB] rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-20 min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#A8A29E] text-lg mb-4">Producto no encontrado</p>
          <Link to="/shop" className="btn-primary">
            Volver a la tienda
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#FAFAF9]" data-testid="product-detail-page">
      <div className="container-luxury py-8 md:py-16">
        {/* Back Link */}
        <Link 
          to="/shop" 
          className="inline-flex items-center space-x-2 text-sm text-[#57534E] hover:text-[#BA7B7C] transition-colors mb-8"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Volver a la tienda</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Product Image */}
          <div className="aspect-square bg-[#F5F0EB] rounded-2xl overflow-hidden">
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="product-image"
            />
          </div>

          {/* Product Info */}
          <div className="lg:py-8">
            <p className="text-sm tracking-[0.2em] uppercase text-[#A8A29E] mb-4">
              {product.category}
            </p>
            
            <h1 
              className="text-4xl md:text-5xl font-light tracking-tight text-[#1C1917] mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="product-name"
            >
              {product.name}
            </h1>

            {/* Rating */}
            {product.review_count > 0 && (
              <div className="flex items-center space-x-2 mb-6">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={`rating-star-${product.id}-${i}`}
                      className={`w-4 h-4 ${i < Math.round(product.average_rating) ? 'text-[#BA7B7C] fill-[#BA7B7C]' : 'text-[#E7E5E4]'}`}
                    />
                  ))}
                </div>
                <span className="text-sm text-[#57534E]">
                  ({product.review_count} reseñas)
                </span>
              </div>
            )}

            <p 
              className="text-3xl font-light text-[#1C1917] mb-8"
              data-testid="product-price"
            >
              {formatPrice(product.price)}
            </p>

            <p className="text-[#57534E] leading-relaxed mb-8" data-testid="product-description">
              {product.description}
            </p>

            {/* Stock Status */}
            {product.stock > 0 ? (
              <div className="flex items-center space-x-2 mb-6">
                {product.stock <= 3 ? (
                  <>
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    <span className="text-sm text-amber-600 font-medium">
                      ¡Solo {product.stock} {product.stock === 1 ? 'disponible' : 'disponibles'}!
                    </span>
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-[#57534E]">
                      En stock ({product.stock} disponibles)
                    </span>
                  </>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2 mb-6">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <span className="text-sm text-red-500 font-medium">
                  Producto agotado
                </span>
              </div>
            )}

            {/* Delivery Info */}
            <div className="bg-[#F5F0EB] rounded-2xl p-5 mb-8">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#BA7B7C] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-[#1C1917] mb-1">
                    Entregas personales
                  </p>
                  <p className="text-xs text-[#57534E] leading-relaxed">
                    Entregas en Plaza Jardín o UTN (Universidad Tecnológica de Nezahualcóyotl). Te contactamos vía WhatsApp.
                  </p>
                </div>
              </div>
            </div>

            {/* Quantity Selector - Only show if in stock */}
            {product.stock > 0 && (
              <div className="mb-8">
                <p className="text-sm text-[#57534E] mb-3">Cantidad</p>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 rounded-full border border-[#E7E5E4] flex items-center justify-center hover:border-[#BA7B7C] transition-colors"
                    data-testid="decrease-quantity"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-lg font-medium w-8 text-center" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                    className="w-10 h-10 rounded-full border border-[#E7E5E4] flex items-center justify-center hover:border-[#BA7B7C] transition-colors"
                    data-testid="increase-quantity"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleAddToCart}
                disabled={addingToCart || product.stock <= 0}
                className={`flex-1 flex items-center justify-center space-x-2 py-4 px-8 rounded-full transition-all ${
                  product.stock <= 0 
                    ? 'bg-[#E7E5E4] text-[#A8A29E] cursor-not-allowed' 
                    : 'btn-primary'
                }`}
                data-testid="add-to-cart-btn"
              >
                <ShoppingBag className="w-5 h-5" />
                <span>
                  {product.stock <= 0 
                    ? 'Agotado' 
                    : addingToCart 
                      ? 'Agregando...' 
                      : 'Agregar al carrito'
                  }
                </span>
              </button>
              <button
                onClick={handleWishlistToggle}
                className={`w-14 h-14 rounded-full border flex items-center justify-center transition-all ${
                  isInWishlist 
                    ? 'border-[#BA7B7C] bg-[#BA7B7C] text-white' 
                    : 'border-[#E7E5E4] hover:border-[#BA7B7C]'
                }`}
                data-testid="wishlist-btn"
              >
                <Heart 
                  className="w-5 h-5" 
                  fill={isInWishlist ? 'white' : 'none'}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-20 pt-20 border-t border-[#E7E5E4]" data-testid="reviews-section">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-12">
            <h2 
              className="text-3xl font-light text-[#1C1917]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Reseñas ({reviews.length})
            </h2>
            {isAuthenticated && !showReviewForm && (
              <button
                onClick={() => setShowReviewForm(true)}
                className="mt-4 md:mt-0 btn-secondary"
                data-testid="write-review-btn"
              >
                Escribir reseña
              </button>
            )}
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <form 
              onSubmit={handleSubmitReview}
              className="bg-white rounded-2xl p-6 md:p-8 mb-8 shadow-soft"
              data-testid="review-form"
            >
              <h3 className="text-lg font-medium text-[#1C1917] mb-6">Tu reseña</h3>
              
              <div className="mb-6">
                <p className="text-sm text-[#57534E] mb-3">Calificación</p>
                <div className="flex space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setReviewRating(star)}
                      className="focus:outline-none"
                      data-testid={`rating-star-${star}`}
                    >
                      <Star
                        className={`w-6 h-6 transition-colors ${
                          star <= reviewRating 
                            ? 'text-[#BA7B7C] fill-[#BA7B7C]' 
                            : 'text-[#E7E5E4]'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-[#57534E] mb-3">Comentario</p>
                <textarea
                  value={reviewComment}
                  onChange={(e) => setReviewComment(e.target.value)}
                  placeholder="Comparte tu experiencia con este producto..."
                  className="w-full h-32 px-4 py-3 rounded-xl border border-[#E7E5E4] focus:outline-none focus:border-[#BA7B7C] resize-none transition-colors"
                  required
                  data-testid="review-comment"
                />
              </div>

              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={submittingReview}
                  className="btn-primary"
                  data-testid="submit-review-btn"
                >
                  {submittingReview ? 'Publicando...' : 'Publicar reseña'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowReviewForm(false)}
                  className="btn-secondary"
                  data-testid="cancel-review-btn"
                >
                  Cancelar
                </button>
              </div>
            </form>
          )}

          {/* Reviews List */}
          {reviews.length === 0 ? (
            <p className="text-center text-[#A8A29E] py-12">
              Aún no hay reseñas para este producto
            </p>
          ) : (
            <div className="space-y-6">
              {reviews.map((review) => (
                <div 
                  key={review.id} 
                  className="bg-white rounded-2xl p-6 shadow-soft"
                  data-testid={`review-${review.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="font-medium text-[#1C1917]">{review.user_name}</p>
                      <div className="flex mt-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={`review-star-${review.id}-${i}`}
                            className={`w-4 h-4 ${i < review.rating ? 'text-[#BA7B7C] fill-[#BA7B7C]' : 'text-[#E7E5E4]'}`}
                          />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-[#A8A29E]">
                      {new Date(review.created_at).toLocaleDateString('es-MX')}
                    </p>
                  </div>
                  <p className="text-[#57534E]">{review.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
